import React, { useEffect, useState } from 'react';
import { API_BASE, authHeaders, jsonHeaders } from '../utils/api';
import './WebCoordinators.css';

const initialFormState = { name: '', email: '', password: '' };

const WebCoordinators = () => {
  const [coordinators, setCoordinators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState(initialFormState);
  const [saving, setSaving] = useState(false);
  const [resetState, setResetState] = useState({ id: null, password: '' });

  const fetchCoordinators = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/users?role=web_coordinator`,
        {
          headers: authHeaders(),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to load coordinators');
      }
      setCoordinators(data.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoordinators();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/users`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to create coordinator');
      }
      setForm(initialFormState);
      await fetchCoordinators();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (id, is_active) => {
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/admin/users/${id}`, {
        method: 'PUT',
        headers: jsonHeaders(),
        body: JSON.stringify({ is_active: !is_active }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to update status');
      }
      await fetchCoordinators();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetState.id || !resetState.password) return;
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/users/${resetState.id}/password`,
        {
          method: 'PATCH',
          headers: jsonHeaders(),
          body: JSON.stringify({ password: resetState.password }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Failed to reset password');
      }
      setResetState({ id: null, password: '' });
    } catch (err) {
      setError(err.message);
    }
  };

  const startReset = (id) => {
    setResetState({ id, password: '' });
  };

  return (
    <div className="webcoordinators-page">
      <div className="page-heading">
        <h1>Web Coordinators</h1>
        <p>Create, deactivate, or reset access for website coordinators.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}

      <div className="webcoordinators-grid">
        <div className="card">
          <h2>Add Coordinator</h2>
          <form onSubmit={handleCreate} className="form-grid">
            <label>
              Full Name
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleChange}
                required
              />
            </label>
            <label>
              Email
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
              />
            </label>
            <label>
              Password
              <input
                type="password"
                name="password"
                value={form.password}
                onChange={handleChange}
                required
                minLength={6}
              />
            </label>
            <button type="submit" disabled={saving}>
              {saving ? 'Creating…' : 'Create Coordinator'}
            </button>
          </form>
        </div>

        <div className="card">
          <h2>Existing Coordinators</h2>
          {loading ? (
            <p>Loading coordinators…</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coordinators.length === 0 && (
                  <tr>
                    <td colSpan="4">No coordinators yet.</td>
                  </tr>
                )}
                {coordinators.map((coordinator) => (
                  <tr key={coordinator.id}>
                    <td>{coordinator.name}</td>
                    <td>{coordinator.email}</td>
                    <td>
                      <span
                        className={
                          coordinator.is_active ? 'status active' : 'status'
                        }
                      >
                        {coordinator.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        onClick={() =>
                          toggleStatus(coordinator.id, coordinator.is_active)
                        }
                      >
                        {coordinator.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        type="button"
                        onClick={() => startReset(coordinator.id)}
                      >
                        Reset Password
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {resetState.id && (
            <form onSubmit={handleResetPassword} className="reset-form">
              <label>
                New Password
                <input
                  type="password"
                  value={resetState.password}
                  onChange={(e) =>
                    setResetState((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  required
                  minLength={6}
                />
              </label>
              <div className="reset-actions">
                <button type="submit">Update Password</button>
                <button
                  type="button"
                  onClick={() => setResetState({ id: null, password: '' })}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebCoordinators;









